import {
  dateFormatFromConfig,
  encodeDateCellValue,
  formatDateValue,
  parseDatePayload,
} from './date-format.js';
import { DATABASE_COLUMN_TYPES, type DatabaseColumnType } from './schema.js';
import {
  resolveOptionByValue,
  selectDisplayLabel,
  type ColumnConfigLike,
} from './select-options.js';

/** Typed cell payload in `block_properties.value` jsonb. */
export type PropertyValuePayload = {
  t: string;
  v?: unknown;
  /** Date range end (ISO date); start remains in `v`. */
  end?: string;
  includeTime?: boolean;
  /** Pre-formatted display for computed rollup cells (server-injected). */
  display?: string;
};

/** Column types with inline editors in Phase 1 (database_implementation.md). */
export const PHASE1_COLUMN_TYPES = [
  'text',
  'number',
  'checkbox',
  'url',
] as const;

export type Phase1ColumnType = (typeof PHASE1_COLUMN_TYPES)[number];

const phase1TypeSet = new Set<string>(PHASE1_COLUMN_TYPES);

export function isPhase1ColumnType(type: string): type is Phase1ColumnType {
  return phase1TypeSet.has(type);
}

export type DecodedCellValue = {
  text: string;
  bool: boolean;
};

function isTextLikeColumnType(type: string): boolean {
  return (
    type === 'text' ||
    type === 'url' ||
    type === 'email' ||
    type === 'phone'
  );
}

/** Legacy email/phone columns behave like text in the UI until retyped. */
export function isEditableColumnType(type: string): boolean {
  return (
    isPhase1ColumnType(type) ||
    type === 'email' ||
    type === 'phone' ||
    type === 'select' ||
    type === 'multi_select' ||
    type === 'date' ||
    type === 'relation'
  );
}

export function isRichColumnType(type: string): boolean {
  return type === 'select' || type === 'multi_select' || type === 'date' || type === 'relation';
}

export function isPropertyValuePayload(value: unknown): value is PropertyValuePayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    't' in value &&
    typeof (value as PropertyValuePayload).t === 'string'
  );
}

export function propertyScalar(value: PropertyValuePayload | null | undefined): string | number | boolean | null {
  if (!value) {
    return null;
  }
  switch (value.t) {
    case 'checkbox':
      return value.v === true;
    case 'number':
      return typeof value.v === 'number' ? value.v : Number(value.v ?? NaN);
    case 'multi_select':
      return Array.isArray(value.v) ? value.v.join('\0') : '';
    case 'relation':
      return Array.isArray(value.v) ? value.v.join('\0') : '';
    default:
      return value.v == null ? null : String(value.v);
  }
}

export function comparePropertyScalars(
  a: string | number | boolean | null,
  b: string | number | boolean | null,
): number {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    if (Number.isNaN(a) && Number.isNaN(b)) {
      return 0;
    }
    if (Number.isNaN(a)) {
      return 1;
    }
    if (Number.isNaN(b)) {
      return -1;
    }
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

/** Encode user input into a stored property payload for the given column type. */
export function encodeCellValue(
  type: string,
  rawInput: string | number | boolean,
): PropertyValuePayload | null {
  if (type === 'checkbox') {
    if (typeof rawInput === 'boolean') {
      return { t: 'checkbox', v: rawInput };
    }
    const s = String(rawInput).trim().toLowerCase();
    if (s === '' || s === 'false' || s === '0' || s === 'no') {
      return { t: 'checkbox', v: false };
    }
    return { t: 'checkbox', v: true };
  }

  if (type === 'number') {
    if (typeof rawInput === 'number') {
      if (Number.isNaN(rawInput)) {
        return null;
      }
      return { t: 'number', v: rawInput };
    }
    const trimmed = String(rawInput).trim();
    if (trimmed === '') {
      return null;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n)) {
      return null;
    }
    return { t: 'number', v: n };
  }

  if (type === 'text' || isTextLikeColumnType(type)) {
    return { t: type, v: String(rawInput).trim() };
  }

  if (type === 'select') {
    const id = String(rawInput).trim();
    return id ? { t: 'select', v: id } : null;
  }

  if ((DATABASE_COLUMN_TYPES as readonly string[]).includes(type)) {
    return { t: type, v: String(rawInput).trim() };
  }

  return { t: type, v: String(rawInput).trim() };
}

export function encodeSelectCellValue(optionId: string | null): PropertyValuePayload | null {
  if (!optionId || !optionId.trim()) {
    return null;
  }
  return { t: 'select', v: optionId.trim() };
}

export function encodeMultiSelectCellValue(
  optionIds: string[],
): PropertyValuePayload | null {
  const ids = optionIds.map((id) => id.trim()).filter(Boolean);
  if (ids.length === 0) {
    return null;
  }
  return { t: 'multi_select', v: ids };
}

export { encodeRelationCellValue, parseRelationIds } from './relation-config.js';

export type CoerceCellValueOptions = {
  columnConfig?: ColumnConfigLike;
};

function isSelectFamily(type: string): boolean {
  return type === 'select' || type === 'multi_select';
}

/** Delimiter for multi-select values in text columns (option names may contain commas). */
export const MULTI_SELECT_TEXT_DELIMITER = ';';

/** Split text into multi-select parts (`;` preferred; `,` supported for legacy cells). */
export function parseMultiSelectPlainText(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  if (trimmed.includes(MULTI_SELECT_TEXT_DELIMITER)) {
    return trimmed
      .split(MULTI_SELECT_TEXT_DELIMITER)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (trimmed.includes(',')) {
    return trimmed
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [trimmed];
}

export function joinMultiSelectPlainText(parts: string[]): string {
  return parts
    .map((s) => s.trim())
    .filter(Boolean)
    .join(`${MULTI_SELECT_TEXT_DELIMITER} `);
}

/** Plain labels for select values (`;`-separated for multi). */
export function selectPayloadToPlainText(
  payload: PropertyValuePayload,
  config?: ColumnConfigLike,
): string {
  if (payload.t === 'select') {
    const v = payload.v == null ? '' : String(payload.v);
    const label = selectDisplayLabel(config, v);
    return (label || v).trim();
  }
  if (payload.t === 'multi_select') {
    const ids = Array.isArray(payload.v) ? payload.v.map(String) : [];
    const labels = ids
      .map((id) => selectDisplayLabel(config, id) || id)
      .map((s) => s.trim())
      .filter(Boolean);
    return joinMultiSelectPlainText(labels);
  }
  return '';
}

function mapLabelToOptionId(label: string, config?: ColumnConfigLike): string {
  const trimmed = label.trim();
  if (!trimmed) {
    return '';
  }
  const opt = resolveOptionByValue(config, trimmed);
  return opt?.id ?? trimmed;
}

function plainTextToSelectValue(text: string, config?: ColumnConfigLike): string | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  const whole = resolveOptionByValue(config, trimmed);
  if (whole) {
    return whole.id;
  }
  const parts = parseMultiSelectPlainText(text);
  const first = parts[0] ?? trimmed;
  if (!first) {
    return null;
  }
  const id = mapLabelToOptionId(first, config);
  return id || null;
}

function plainTextToMultiSelectValues(text: string, config?: ColumnConfigLike): string[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  const whole = resolveOptionByValue(config, trimmed);
  if (whole) {
    return [whole.id];
  }
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const part of parseMultiSelectPlainText(text)) {
    const id = mapLabelToOptionId(part, config);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

function selectIdToMultiIds(
  storedId: string,
  fromConfig?: ColumnConfigLike,
  toConfig?: ColumnConfigLike,
): string[] {
  const raw = storedId.trim();
  if (!raw) {
    return [];
  }
  const label = selectDisplayLabel(fromConfig, raw) || raw;
  const id = mapLabelToOptionId(label, toConfig);
  return id ? [id] : [];
}

function multiIdsToSelectId(
  ids: string[],
  fromConfig?: ColumnConfigLike,
  toConfig?: ColumnConfigLike,
): string | null {
  const first = ids.map(String).find((id) => id.trim());
  if (!first) {
    return null;
  }
  const label = selectDisplayLabel(fromConfig, first) || first;
  return mapLabelToOptionId(label, toConfig) || null;
}

/** Coerce a cell when a column's type changes (uses option labels, not raw ids). */
export function coerceCellValueForTypeChange(
  payload: PropertyValuePayload | null | undefined,
  fromType: string,
  toType: string,
  fromConfig?: ColumnConfigLike,
  toConfig?: ColumnConfigLike,
): PropertyValuePayload | null {
  if (!payload || !isPropertyValuePayload(payload)) {
    return null;
  }

  const involvesSelect =
    isSelectFamily(fromType) ||
    isSelectFamily(toType) ||
    payload.t === 'select' ||
    payload.t === 'multi_select';

  if (!involvesSelect) {
    return coerceCellValue(payload, toType, { columnConfig: toConfig });
  }

  if (fromType === 'select' && toType === 'multi_select' && payload.t === 'select') {
    const raw = payload.v == null ? '' : String(payload.v);
    const ids = selectIdToMultiIds(raw, fromConfig, toConfig);
    return ids.length > 0 ? { t: 'multi_select', v: ids } : null;
  }

  if (fromType === 'multi_select' && toType === 'select' && payload.t === 'multi_select') {
    const ids = Array.isArray(payload.v) ? payload.v.map(String) : [];
    const id = multiIdsToSelectId(ids, fromConfig, toConfig);
    return id ? { t: 'select', v: id } : null;
  }

  let plain = '';
  if (payload.t === 'select' || payload.t === 'multi_select') {
    plain = selectPayloadToPlainText(payload, fromConfig);
  } else {
    plain = payload.v == null ? '' : String(payload.v).trim();
  }

  if (toType === 'select') {
    const id = plainTextToSelectValue(plain, toConfig);
    return id ? { t: 'select', v: id } : null;
  }

  if (toType === 'multi_select') {
    const ids = plainTextToMultiSelectValues(plain, toConfig);
    return ids.length > 0 ? { t: 'multi_select', v: ids } : null;
  }

  if (toType === 'text') {
    return plain === '' ? null : { t: 'text', v: plain };
  }

  if (plain === '') {
    return null;
  }
  return coerceCellValue({ t: 'text', v: plain }, toType, { columnConfig: toConfig });
}

/** Decode a stored payload for display/editing in the UI. */
export function decodeCellValue(
  columnType: string,
  payload: PropertyValuePayload | null | undefined,
  options?: CoerceCellValueOptions,
): DecodedCellValue {
  if (!payload || !isPropertyValuePayload(payload)) {
    return { text: '', bool: false };
  }

  const coerced = coerceCellValue(payload, columnType, options);
  if (!coerced) {
    return { text: '', bool: false };
  }

  if (columnType === 'checkbox') {
    return { text: '', bool: coerced.v === true };
  }

  if (columnType === 'number') {
    if (typeof coerced.v === 'number' && !Number.isNaN(coerced.v)) {
      return { text: String(coerced.v), bool: false };
    }
    return { text: '', bool: false };
  }

  if (columnType === 'multi_select') {
    if (coerced.t === 'multi_select' && Array.isArray(coerced.v)) {
      const labels = coerced.v
        .map(String)
        .map((id) => selectDisplayLabel(options?.columnConfig, id) || id);
      return { text: joinMultiSelectPlainText(labels), bool: false };
    }
    if (coerced.t === 'text' && coerced.v != null) {
      return { text: String(coerced.v), bool: false };
    }
    const ids = Array.isArray(coerced.v) ? coerced.v.map(String) : [];
    return { text: joinMultiSelectPlainText(ids), bool: false };
  }

  if (columnType === 'relation') {
    const ids = Array.isArray(coerced.v) ? coerced.v.map(String) : [];
    return { text: ids.join(','), bool: false };
  }

  if (columnType === 'select' && coerced.t === 'select') {
    const v = coerced.v == null ? '' : String(coerced.v);
    const label = selectDisplayLabel(options?.columnConfig, v);
    return { text: label || v, bool: false };
  }

  if (columnType === 'date') {
    const { start, end } = parseDatePayload(coerced);
    if (!start) {
      return { text: '', bool: false };
    }
    return { text: end && end !== start ? `${start}..${end}` : start, bool: false };
  }

  return {
    text: coerced.v == null ? '' : String(coerced.v),
    bool: false,
  };
}

/**
 * Reinterpret a stored payload for a column type (e.g. after type change).
 * Select/multi_select use option labels when converting to text; text uses option
 * names when converting back when possible.
 */
export function coerceCellValue(
  payload: PropertyValuePayload | null | undefined,
  newType: string,
  options?: CoerceCellValueOptions,
): PropertyValuePayload | null {
  if (!payload || !isPropertyValuePayload(payload)) {
    return null;
  }

  const config = options?.columnConfig;
  const fromT = payload.t;

  if (
    (fromT === 'select' || fromT === 'multi_select') &&
    !isSelectFamily(newType)
  ) {
    const plain = selectPayloadToPlainText(payload, config);
    if (plain === '') {
      return null;
    }
    return coerceCellValue({ t: 'text', v: plain }, newType, options);
  }

  if (newType === 'select') {
    let plain = '';
    if (fromT === 'select' || fromT === 'multi_select') {
      plain = selectPayloadToPlainText(payload, config);
    } else {
      const scalar = propertyScalar(payload);
      plain = scalar == null ? '' : String(scalar).trim();
    }
    const id = plainTextToSelectValue(plain, config);
    return id ? { t: 'select', v: id } : null;
  }

  if (newType === 'multi_select') {
    let plain = '';
    if (fromT === 'select' || fromT === 'multi_select') {
      plain = selectPayloadToPlainText(payload, config);
    } else if (payload.t === 'text' && payload.v != null) {
      plain = String(payload.v).trim();
    } else {
      const scalar = propertyScalar(payload);
      plain = scalar == null ? '' : String(scalar).trim();
    }
    const ids = plainTextToMultiSelectValues(plain, config);
    return ids.length > 0 ? { t: 'multi_select', v: ids } : null;
  }

  const scalar = propertyScalar(payload);
  if (scalar === null) {
    return null;
  }

  if (newType === 'checkbox') {
    if (typeof scalar === 'boolean') {
      return { t: 'checkbox', v: scalar };
    }
    if (typeof scalar === 'number') {
      return { t: 'checkbox', v: scalar !== 0 && !Number.isNaN(scalar) };
    }
    const s = String(scalar).trim().toLowerCase();
    if (s === '' || s === 'false' || s === '0' || s === 'no') {
      return { t: 'checkbox', v: false };
    }
    return { t: 'checkbox', v: true };
  }

  if (newType === 'number') {
    const n = typeof scalar === 'number' ? scalar : Number(String(scalar).trim());
    if (Number.isNaN(n)) {
      return null;
    }
    return { t: 'number', v: n };
  }

  if (isTextLikeColumnType(newType)) {
    return { t: newType, v: String(scalar) };
  }

  if (newType === 'text') {
    return { t: 'text', v: String(scalar) };
  }

  if (newType === 'select') {
    const id = plainTextToSelectValue(
      payload.t === 'text' && payload.v != null ? String(payload.v) : String(scalar),
      config,
    );
    return id ? { t: 'select', v: id } : null;
  }

  if (newType === 'multi_select') {
    const ids = plainTextToMultiSelectValues(
      payload.t === 'text' && payload.v != null ? String(payload.v) : String(scalar),
      config,
    );
    return ids.length > 0 ? { t: 'multi_select', v: ids } : null;
  }

  if (newType === 'date') {
    const existing = parseDatePayload(payload);
    if (existing.start) {
      return encodeDateCellValue(existing.start, existing.end);
    }
    const s = String(scalar).trim();
    return s ? { t: 'date', v: s } : null;
  }

  if ((DATABASE_COLUMN_TYPES as readonly string[]).includes(newType)) {
    return { t: newType, v: String(scalar) };
  }

  return { t: newType, v: String(scalar) };
}

export function formatCellDisplay(
  columnType: string,
  payload: PropertyValuePayload | Record<string, unknown> | null | undefined,
  options?: { columnConfig?: Record<string, unknown> },
): string {
  if (!payload || typeof payload !== 'object' || !('t' in payload)) {
    return '';
  }
  const p = payload as PropertyValuePayload;
  if (columnType === 'checkbox') {
    const decoded = decodeCellValue(columnType, p);
    return decoded.bool ? 'Yes' : 'No';
  }
  if (columnType === 'select') {
    const v = p.v == null ? '' : String(p.v);
    return selectDisplayLabel(options?.columnConfig, v);
  }
  if (columnType === 'multi_select') {
    const ids = Array.isArray(p.v) ? p.v.map(String) : [];
    if (ids.length === 0) {
      return '';
    }
    return ids
      .map((id) => selectDisplayLabel(options?.columnConfig, id))
      .filter(Boolean)
      .join(', ');
  }
  if (columnType === 'date') {
    const format = dateFormatFromConfig(options?.columnConfig);
    return formatDateValue(p, format);
  }
  if (columnType === 'relation') {
    return '';
  }
  if (columnType === 'rollup' && p.t === 'rollup') {
    if (typeof p.display === 'string' && p.display.length > 0) {
      return p.display;
    }
    if (p.v == null) {
      return '';
    }
    return String(p.v);
  }
  const decoded = decodeCellValue(columnType, p);
  if (decoded.text === '') {
    return '';
  }
  return decoded.text;
}

export function allDatabaseColumnTypes(): readonly DatabaseColumnType[] {
  return DATABASE_COLUMN_TYPES;
}
